import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Company from "marketplace/companies/company";
import { z } from "zod";

export type MarketplaceCompanyAPIRequestJSON = z.infer<
  typeof MarketplaceCompanyAPIRequestSchema.MarketplaceCompanyAPIRequestZ
>;

const AddressRequestZ = z
  .object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
  })
  .optional();

export default class MarketplaceCompanyAPIRequestSchema {
  public static readonly MarketplaceCompanyAPIRequestZ = z.object({
    name: z.string(),
    yearEstablished: z.number().optional(),
    numberOfEmployees: z.number().optional(),
    description: z.string().optional(),
    address: AddressRequestZ,
    website: z.string().optional(),
    categories: z.array(z.string()),
    isVisible: z.boolean(),
    contactId: z.uuid().optional(),
  });

  public static validateJSON(json: unknown): MarketplaceCompanyAPIRequestJSON {
    return parseOrThrow(MarketplaceCompanyAPIRequestSchema.MarketplaceCompanyAPIRequestZ, json, {
      logError: true,
      schemaName: "MarketplaceCompanyAPIRequestSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceCompanyAPIRequestJSON {
    return MarketplaceCompanyAPIRequestSchema.MarketplaceCompanyAPIRequestZ.safeParse(json).success;
  }

  public static serialize(company: Company): MarketplaceCompanyAPIRequestJSON {
    if (!company.profile?.name) throw new Error("Company name is required");
    return MarketplaceCompanyAPIRequestSchema.validateJSON({
      name: company.profile.name,
      yearEstablished: company.profile.yearEstablished,
      numberOfEmployees: company.profile.numberOfEmployees,
      description: company.profile.description,
      address: company.profile.location
        ? {
            address1: company.profile.location.address1,
            address2: company.profile.location.address2,
            city: company.profile.location.city,
            state: company.profile.location.state,
            postalCode: company.profile.location.postalCode,
            country: company.profile.location.country,
          }
        : undefined,
      website: company.profile.website?.toString(),
      categories: company.profile.categories,
      isVisible: company.isVisible,
      contactId: company.contactId?.value,
    });
  }
}
